import { uniq } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { BonusProposalData } from '~/data/proposals';
import { BonusProgramConfig } from '~/data/rewarding/types';
import {
  CommonEmployeeProposalFormData,
  NON_PRESENT_CONFIG,
  mapDefaultDataUtils,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { convertToDateOrNull } from '~/utils/helpers/date';

export type BonusProposalFormData = CommonEmployeeProposalFormData & {
  bonusType: string;
  bonusAmount: number;
  paymentPeriodInfo: string;
  paymentPeriodStartDate?: Date | null;
  paymentPeriodEndDate?: Date | null;
  recurrence?: number | null;
  paymentDate: Date | null;
  info: string;
};
export const mapDefaultData = (
  targetEmploymentId: number,
  proposalData?: BonusProposalData,
  defaultResponsibleHrId?: number,
  defaultBonusType?: string,
): BonusProposalFormData => {
  const { defaultToEmptyString, defaultResponsibleHR, defaultToNumber } =
    mapDefaultDataUtils();

  return {
    employmentId: defaultToEmptyString(
      proposalData?.employmentFK || targetEmploymentId,
    ),
    bonusType: defaultToEmptyString(
      proposalData?.incentiveShortTypeFK || defaultBonusType,
    ),
    bonusAmount: defaultToNumber(proposalData?.paymentAmount, 0),
    paymentPeriodInfo: defaultToEmptyString(proposalData?.paymentPeriod),
    paymentPeriodStartDate: convertToDateOrNull(
      proposalData?.paymentPeriodStartDate,
    ),
    paymentPeriodEndDate: convertToDateOrNull(
      proposalData?.paymentPeriodEndDate,
    ),
    recurrence: proposalData?.recurrence ?? null,
    paymentDate: convertToDateOrNull(proposalData?.paymentDate),
    info: defaultToEmptyString(proposalData?.info),
    responsibleHR: defaultResponsibleHR(
      proposalData?.hrFK,
      defaultResponsibleHrId,
    ),
  };
};

export const useValidationSchema = (
  isEmploymentIdRequired: boolean,
  bonusPrograms: BonusProgramConfig[],
) => {
  const { t } = useTranslation();

  const validBonusTypes = useMemo(
    () =>
      uniq(
        bonusPrograms
          .filter((p) => p.fEmplSubUId !== NON_PRESENT_CONFIG)
          .map((p) => p.fIncentiveShortType),
      ),
    [bonusPrograms],
  );

  return yup.object().shape<BonusProposalFormData>({
    employmentId: isEmploymentIdRequired
      ? yup.string().required(t('fieldIsRequired'))
      : yup.string().default(''),
    bonusType: yup
      .string()
      .required(t('fieldIsRequired'))
      .oneOf(validBonusTypes, t('selectedOptionNotAvailableSelectAnother')),
    bonusAmount: yup
      .number()
      .typeError(t('laValueNotValidNumber'))
      .required(t('fieldIsRequired')),
    paymentPeriodInfo: yup.string(),
    paymentPeriodStartDate: yup.date().nullable(),
    paymentPeriodEndDate: yup.date().nullable(),
    recurrence: yup.number().typeError(t('laValueNotValidNumber')).nullable(),
    paymentDate: yup.date().required(t('fieldIsRequired')).nullable(),
    info: yup.string(),
    responsibleHR: yup.number().required(t('fieldIsRequired')),
  });
};
